/**
 * Keyboard shortcuts: a fixed, documented set of *named actions* with default bindings.
 * Bindings are data (remappable via the `shortcuts` preference); handlers are registered by
 * components while they are mounted. No executable code in bindings (legacy KEY_BINDING
 * `eval|js` is gone).
 *
 * Scopes: `global` < `table` < `record`. A key resolves to the action of the highest active scope
 * that has a handler; while a `record` (overlay) is active, `table` actions are suppressed.
 * In text inputs only bindings with Ctrl/Alt/Meta (and Escape) fire.
 */
import { getCurrentInstance, onBeforeUnmount, reactive } from 'vue';

export type ShortcutScope = 'global' | 'table' | 'record';
const SCOPE_RANK: Record<ShortcutScope, number> = { global: 0, table: 1, record: 2 };

export interface ActionDef {
  scope: ShortcutScope;
  /** i18n key of the description */
  label: string;
  keys: string[];
}

export const DEFAULT_ACTIONS = {
  'app.search': { scope: 'global', label: 'shortcuts.app_search', keys: ['mod+k', '/'] },
  'app.toggleSidebar': { scope: 'global', label: 'shortcuts.app_toggleSidebar', keys: ['mod+b'] },
  'app.help': { scope: 'global', label: 'shortcuts.app_help', keys: ['?'] },
  'table.next': { scope: 'table', label: 'shortcuts.table_next', keys: ['j', 'arrowdown'] },
  'table.prev': { scope: 'table', label: 'shortcuts.table_prev', keys: ['k', 'arrowup'] },
  'table.left': { scope: 'table', label: 'shortcuts.table_left', keys: ['arrowleft'] },
  'table.right': { scope: 'table', label: 'shortcuts.table_right', keys: ['arrowright'] },
  'table.open': { scope: 'table', label: 'shortcuts.table_open', keys: ['enter', 'o'] },
  'table.select': { scope: 'table', label: 'shortcuts.table_select', keys: ['space', 'x'] },
  'table.new': { scope: 'table', label: 'shortcuts.table_new', keys: ['alt+n', 'n', 'insert'] },
  'table.delete': { scope: 'table', label: 'shortcuts.table_delete', keys: ['delete'] },
  'table.refresh': { scope: 'table', label: 'shortcuts.table_refresh', keys: ['alt+r', 'r'] },
  'table.search': { scope: 'table', label: 'shortcuts.table_search', keys: ['f'] },
  'table.filter': { scope: 'table', label: 'shortcuts.table_filter', keys: ['alt+f'] },
  'table.pageNext': { scope: 'table', label: 'shortcuts.table_pageNext', keys: ['alt+arrowright'] },
  'table.pagePrev': { scope: 'table', label: 'shortcuts.table_pagePrev', keys: ['alt+arrowleft'] },
  'table.print': { scope: 'table', label: 'shortcuts.table_print', keys: ['alt+p'] },
  'record.save': { scope: 'record', label: 'shortcuts.record_save', keys: ['mod+s'] },
  'record.saveClose': { scope: 'record', label: 'shortcuts.record_saveClose', keys: ['mod+enter'] },
  'record.close': { scope: 'record', label: 'shortcuts.record_close', keys: ['escape'] },
  'record.next': { scope: 'record', label: 'shortcuts.record_next', keys: ['alt+arrowdown', 'alt+pagedown'] },
  'record.prev': { scope: 'record', label: 'shortcuts.record_prev', keys: ['alt+arrowup', 'alt+pageup'] },
  'record.print': { scope: 'record', label: 'shortcuts.record_print', keys: ['alt+p'] },
  // legacy KEY_BINDING 18-67 (Alt+C): compare the record with its live counterpart (view.compare)
  'record.compare': { scope: 'record', label: 'shortcuts.record_compare', keys: ['alt+c'] },
  // Ctrl+Tab / Ctrl+Shift+Tab (legacy) switch browser tabs before a page sees them: kept as the
  // secondary binding (works where the browser passes it on, e.g. installed PWA), Ctrl+Alt+→/← works everywhere
  'record.pageNext': { scope: 'record', label: 'shortcuts.record_pageNext', keys: ['ctrl+alt+arrowright', 'ctrl+tab'] },
  'record.pagePrev': { scope: 'record', label: 'shortcuts.record_pagePrev', keys: ['ctrl+alt+arrowleft', 'ctrl+shift+tab'] },
} satisfies Record<string, ActionDef>;

export type ActionName = keyof typeof DEFAULT_ACTIONS;
export type ShortcutHandler = (e: KeyboardEvent) => void | boolean;

const MODS = ['ctrl', 'alt', 'shift', 'meta'] as const;
const ALIASES: Record<string, string> = { esc: 'escape', del: 'delete', return: 'enter', space: ' ', up: 'arrowup', down: 'arrowdown', left: 'arrowleft', right: 'arrowright', cmd: 'meta', control: 'ctrl', option: 'alt' };

export const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Canonical form of a binding: modifiers in fixed order, `mod` → ctrl / meta (Mac). */
export function normalizeBinding(binding: string, mac = isMac()): string {
  const parts = binding.toLowerCase().split('+').map((p) => p.trim());
  // "ctrl++" → key "+"
  if (binding.endsWith('++')) parts.splice(parts.length - 2, 2, '+');
  const key = ALIASES[parts[parts.length - 1]!] ?? parts[parts.length - 1]!;
  const mods = new Set(parts.slice(0, -1).map((m) => (m === 'mod' ? (mac ? 'meta' : 'ctrl') : (ALIASES[m] ?? m))));
  return [...MODS.filter((m) => mods.has(m)), key].join('+');
}

/** Canonical form of a key event. Shift is folded into printable non-letter keys (`?`, `/`). */
export function eventBinding(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey'>): string {
  let key = e.key.toLowerCase();
  if (key === 'spacebar') key = ' ';
  const printableSymbol = key.length === 1 && !/[a-z0-9]/.test(key);
  const mods: string[] = [];
  if (e.ctrlKey) mods.push('ctrl');
  if (e.altKey) mods.push('alt');
  if (e.shiftKey && !printableSymbol) mods.push('shift');
  if (e.metaKey) mods.push('meta');
  return [...mods, key].join('+');
}

function isTextTarget(t: EventTarget | null): boolean {
  if (!t || typeof (t as Element).closest !== 'function') return false;
  const el = t as HTMLElement;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes((el as HTMLInputElement).type);
  return false;
}

export class ShortcutService {
  readonly actions: Record<string, ActionDef>;
  /** effective bindings (canonical) per action, reactive for the help dialog */
  readonly bindings: Record<string, string[]> = reactive({});
  private handlers = new Map<string, ShortcutHandler[]>();
  private activeScopes = new Map<ShortcutScope, number>([['global', 1]]);
  private keymap = new Map<string, string[]>();
  private target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | undefined;
  private listener = (e: Event) => this.handle(e as KeyboardEvent);

  constructor(
    actions: Record<string, ActionDef> = DEFAULT_ACTIONS,
    private mac = isMac(),
  ) {
    this.actions = { ...actions };
    this.setOverrides({});
  }

  /** Apply user remappings (`{ "record.save": ["ctrl+s", "f2"] }`; [] disables an action). */
  setOverrides(overrides: Record<string, string[] | undefined>): void {
    for (const [name, def] of Object.entries(this.actions)) {
      const keys = Array.isArray(overrides[name]) ? overrides[name]!.filter((k) => typeof k === 'string' && k.trim()) : def.keys;
      this.bindings[name] = keys.map((k) => normalizeBinding(k, this.mac));
    }
    this.keymap.clear();
    for (const [name, keys] of Object.entries(this.bindings)) {
      for (const k of keys) this.keymap.set(k, [...(this.keymap.get(k) ?? []), name]);
    }
  }

  /** Register a handler (latest wins); returns the unregister function. */
  on(action: string, handler: ShortcutHandler): () => void {
    const list = this.handlers.get(action) ?? [];
    list.push(handler);
    this.handlers.set(action, list);
    return () => {
      const l = this.handlers.get(action);
      const i = l?.lastIndexOf(handler) ?? -1;
      if (l && i >= 0) l.splice(i, 1);
    };
  }

  /** Activate a scope (ref-counted); returns the deactivate function. */
  enterScope(scope: ShortcutScope): () => void {
    this.activeScopes.set(scope, (this.activeScopes.get(scope) ?? 0) + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const n = (this.activeScopes.get(scope) ?? 1) - 1;
      if (n <= 0 && scope !== 'global') this.activeScopes.delete(scope);
      else this.activeScopes.set(scope, n);
    };
  }

  private topScope(): number {
    return Math.max(...[...this.activeScopes.keys()].map((s) => SCOPE_RANK[s]));
  }

  /** Run an action programmatically (menus, buttons); false if no handler. */
  trigger(action: string, e?: KeyboardEvent): boolean {
    const h = this.handlers.get(action);
    const fn = h?.[h.length - 1];
    if (!fn) return false;
    return fn(e ?? new KeyboardEvent('keydown')) !== false;
  }

  /** The action a key event resolves to (or undefined). */
  resolve(e: KeyboardEvent): string | undefined {
    if (e.isComposing || e.key === 'Unidentified' || ['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) return undefined;
    const binding = eventBinding(e);
    const candidates = this.keymap.get(binding);
    if (!candidates?.length) return undefined;
    const inText = isTextTarget(e.target);
    if (inText && !(e.ctrlKey || e.metaKey || e.altKey || binding === 'escape')) return undefined;
    const top = this.topScope();
    let best: { name: string; rank: number } | undefined;
    for (const name of candidates) {
      const def = this.actions[name];
      if (!def || !this.activeScopes.has(def.scope) || !this.handlers.get(name)?.length) continue;
      const rank = SCOPE_RANK[def.scope];
      // a record overlay is modal: table actions do not reach the table underneath
      if (top === SCOPE_RANK.record && def.scope === 'table') continue;
      if (!best || rank > best.rank) best = { name, rank };
    }
    return best?.name;
  }

  handle(e: KeyboardEvent): void {
    if (e.defaultPrevented) return;
    const name = this.resolve(e);
    if (!name) return;
    if (this.trigger(name, e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  attach(target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> = window): void {
    this.detach();
    this.target = target;
    target.addEventListener('keydown', this.listener);
  }

  detach(): void {
    this.target?.removeEventListener('keydown', this.listener);
    this.target = undefined;
  }

  /** Human readable binding (`mod+s` → `Ctrl+S` / `⌘S`). */
  format(binding: string): string {
    const names: Record<string, string> = this.mac
      ? { ctrl: '⌃', alt: '⌥', shift: '⇧', meta: '⌘', arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→', enter: '↩', escape: 'Esc', delete: 'Del' }
      : { ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', meta: 'Win', arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→', enter: 'Enter', escape: 'Esc', delete: 'Entf' };
    return binding
      .split('+')
      .map((p) => names[p] ?? (p.length === 1 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
      .join(this.mac ? '' : '+');
  }
}

export const shortcuts = new ShortcutService();

/**
 * Component helper: activate `scope` and register handlers while the component is mounted.
 * `useShortcuts('table', { 'table.next': () => …, 'table.open': () => … })`
 */
export function useShortcuts(scope: ShortcutScope, handlers: Partial<Record<ActionName, ShortcutHandler>>, service = shortcuts): void {
  const offs = [service.enterScope(scope), ...Object.entries(handlers).map(([a, h]) => service.on(a, h as ShortcutHandler))];
  if (getCurrentInstance()) onBeforeUnmount(() => offs.forEach((f) => f()));
}
